import { EventType, IncrementalSource, MouseInteractionType, NativeInteractionType, PluginName, isNetworkFailure, pageOrigin, } from '@replayops/rrweb-schema';
const RAGE_WINDOW_MS = 1000;
const RAGE_RADIUS_PX = 30;
const RAGE_MIN_CLICKS = 3;
/**
 * Pure timeline construction from an event stream — no DOM required, so it is
 * fully unit-testable. The player renders these markers on the scrubber so a
 * reviewer can jump straight to clicks, errors, rage-clicks, and navigations.
 */
export function buildTimeline(events) {
    const sorted = [...events].sort((a, b) => a.timestamp - b.timestamp);
    const startTime = sorted[0]?.timestamp ?? 0;
    const endTime = sorted[sorted.length - 1]?.timestamp ?? startTime;
    const markers = [];
    const clickTimes = [];
    const origin = pageOrigin(sorted);
    for (const e of sorted) {
        const offsetMs = e.timestamp - startTime;
        if (e.type === EventType.Meta) {
            const href = e.data.href ?? '';
            markers.push({ offsetMs, type: 'navigation', label: shortUrl(href) });
        }
        else if (e.type === EventType.Custom) {
            const data = e.data;
            const tag = data.tag ?? 'event';
            if (!tag.startsWith('$')) {
                // Surface a human-readable detail (e.g. the page_view path) so the
                // reviewer can click straight to a step in the journey.
                const detail = data.payload?.path ?? data.payload?.url ?? data.payload?.name;
                const label = detail ? `${tag} ${String(detail)}` : tag;
                markers.push({ offsetMs, type: 'custom', label });
            }
        }
        else if (e.type === EventType.Plugin && isErrorPlugin(e, origin)) {
            markers.push({ offsetMs, type: 'error', label: errorLabel(e) });
        }
        else if (isClick(e)) {
            const d = e.data;
            clickTimes.push({ t: e.timestamp, x: d.x, y: d.y });
            markers.push({ offsetMs, type: 'click', label: 'Click' });
        }
        else if (e.type === EventType.NativeInteraction) {
            // Native sessions feed the same marker set: a tap is a click and a screen
            // change is a navigation, so rage detection and the scrubber work on a
            // mobile recording without a second implementation.
            const d = e.data;
            if (d.kind === NativeInteractionType.ScreenChange) {
                markers.push({ offsetMs, type: 'navigation', label: d.screen ?? 'Screen' });
            }
            else if (isTap(d)) {
                clickTimes.push({ t: e.timestamp, x: d.x, y: d.y });
                markers.push({ offsetMs, type: 'click', label: d.target ? `Tap · ${d.target}` : 'Tap' });
            }
        }
    }
    // Upgrade click clusters to rage-click markers.
    for (const rage of detectRageClusters(clickTimes)) {
        markers.push({ offsetMs: rage - startTime, type: 'rageClick', label: 'Rage click' });
    }
    markers.sort((a, b) => a.offsetMs - b.offsetMs);
    return { startTime, endTime, durationMs: Math.max(0, endTime - startTime), markers };
}
function isClick(e) {
    if (e.type !== EventType.IncrementalSnapshot)
        return false;
    const d = e.data;
    return (d.source === IncrementalSource.MouseInteraction &&
        d.type === MouseInteractionType.Click &&
        typeof d.x === 'number' &&
        typeof d.y === 'number');
}
/** A discrete touch with coordinates — scrolls and swipes aren't taps. */
function isTap(d) {
    const tapLike = d.kind === NativeInteractionType.Tap ||
        d.kind === NativeInteractionType.DoubleTap ||
        d.kind === NativeInteractionType.LongPress;
    return tapLike && typeof d.x === 'number' && typeof d.y === 'number';
}
function isErrorPlugin(e, origin) {
    const plugin = e.data.plugin;
    if (plugin === PluginName.Error)
        return true;
    if (plugin === PluginName.Console)
        return e.data.payload?.level === 'error';
    if (plugin === PluginName.Network) {
        const payload = e.data.payload;
        // Third-party opaque/blocked beacons (e.g. analytics) report status 0 but
        // aren't user friction — only count first-party failures and real 5xx.
        return isNetworkFailure(payload?.status ?? 0, payload?.url, origin);
    }
    return false;
}
function errorLabel(e) {
    const payload = e.data.payload;
    return (payload?.message ?? payload?.url ?? 'Error').slice(0, 80);
}
/** Returns the timestamp of the first click in each rage cluster. */
function detectRageClusters(clicks) {
    const clusters = [];
    for (let i = 0; i < clicks.length; i++) {
        const anchor = clicks[i];
        let count = 1;
        for (let j = i + 1; j < clicks.length; j++) {
            const c = clicks[j];
            if (c.t - anchor.t > RAGE_WINDOW_MS)
                break;
            if (Math.hypot(c.x - anchor.x, c.y - anchor.y) <= RAGE_RADIUS_PX)
                count++;
            if (count >= RAGE_MIN_CLICKS) {
                clusters.push(anchor.t);
                break;
            }
        }
    }
    return clusters;
}
function shortUrl(href) {
    try {
        const u = new URL(href);
        return u.pathname + u.search;
    }
    catch {
        return href.slice(0, 60);
    }
}
//# sourceMappingURL=timeline.js.map